import React from "react";
import { Alert, Tag } from "antd";
import { CloudUploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { STATES, useOfflineState } from "./index";
import { offlineMarker } from "./resource";
import "./offline.css";

const TAGS = {
  [STATES.PENDING]: { color: "warning", label: "Not synced" },
  [STATES.AUTH]: { color: "warning", label: "Not synced" },
  [STATES.CONFLICT]: { color: "error", label: "Conflict" },
  [STATES.ERROR]: { color: "error", label: "Sync failed" }
};

// What still waits in the outbox for this record, live: the banner goes away once it is synced
const useMarker = (module, id) => {
  const { items } = useOfflineState();
  return id ? offlineMarker(items.filter(item => item.entity === `${module}:${id}`)) : null;
};

/** Small tag for list rows of records with changes waiting in the outbox. */
export const PendingTag = ({ module, record }) => {
  const { t } = useTranslation("dashboard");
  const offline = useMarker(module, record?._id);
  if (!offline) return null;
  const tag = TAGS[offline.state] || TAGS[STATES.PENDING];
  return (
    <Tag color={tag.color} icon={<CloudUploadOutlined />} className="offline-tag">
      {t(tag.label)}
    </Tag>
  );
};

/**
 * Banner of an editor: the record (or parts of it) is only on this device, its upload failed,
 * or what is shown is the copy saved for offline use.
 */
const OfflineNotice = ({ module, record }) => {
  const { t } = useTranslation("dashboard");
  const offline = useMarker(module, record?._id);
  if (!record) return null;
  if (offline) {
    const failed = [STATES.CONFLICT, STATES.ERROR].includes(offline.state);
    return (
      <Alert
        className="offline-notice"
        type={failed ? "error" : "warning"}
        showIcon
        icon={<CloudUploadOutlined />}
        message={
          failed
            ? t("Your offline changes could not be uploaded")
            : t("Saved on this device – not synced yet")
        }
        description={
          failed
            ? offline.error?.message
              ? t(offline.error.message)
              : t("Open the pending changes in the top bar to retry or discard them.")
            : t("It is uploaded automatically as soon as there is a connection.")
        }
      />
    );
  }
  if (record.offlineCopyAt) {
    return (
      <Alert
        className="offline-notice"
        type="info"
        showIcon
        message={t("Offline copy from {{time}}", {
          time: dayjs(record.offlineCopyAt).format("DD.MM. HH:mm")
        })}
      />
    );
  }
  return null;
};

export default OfflineNotice;
